import {
  IBoardCaseInputModel,
  BoardCaseStatusEnum,
  BoardCaseTypeEnum,
  IBoardCaseDetailModel,
  IBoardCasePostModel,
  ICasePutModel,
  IBoardCasesList,
} from 'Modules/Boardwork/Meetings/shared/models/BoardCase'
import { formatCaseTypeToEnum } from 'Modules/Boardwork/Meetings/shared/utils/getCaseStatus'
import { IEmailModel } from 'Modules/Communication/Email/shared/models/Email'
import { MessageTypes } from 'Modules/Communication/shared/models/Inbox'
import { ISmsModel } from 'Modules/Communication/Sms/shared/models/Sms'
import { decodeHtmlEntities } from 'shared/utils/html-helpers'
import { IMeetingAgendaModel } from '../models/BoardMeeting'
import DOMPurify, { Config } from 'dompurify'

export const mapInitialCaseValues = (
  data: IBoardCaseDetailModel | null,
  type?: string,
  message?: IEmailModel | ISmsModel | null,
  messageType?: MessageTypes
) => ({
  id: data?.id || '',
  title: data?.title || (message as IEmailModel)?.title || '',
  description:
    data?.description ||
    (message && getEmailDescription(message, messageType)) ||
    '',
  caseType: type
    ? formatCaseTypeToEnum(type)
    : data?.caseType || BoardCaseTypeEnum.DecisionCase,
  responsible: data?.responsible || null,
  submittedBy: data?.submittedBy || '',
  connectedMeeting: data?.connectedMeeting || null,
  connectedMeetings: data?.connectedMeetings || [],
  link: '',
  decisions: [],
  links: data?.links.map((x) => x.link) || [],
  suggestedDecision: data?.suggestedDecision || '',
  caseStatus: data?.caseStatus || BoardCaseStatusEnum.New,
  deadline: data?.deadline ? new Date(data.deadline) : undefined,
  lastUpdated: data?.lastUpdated,
  ...(message && {
    inboxMessageModel: {
      inboxMessageId: message.id,
      messageType: messageType === 'email' ? 'Email' : 'Sms',
    },
  }),
})

export const mapBoardCaseToNewCaseCreation = (
  boardCase: IBoardCaseInputModel,
  clientId: string,
  // If this is a new case created within a meeting, we need to add the meetingId
  meetingId?: string
): IBoardCasePostModel => ({
  clientId: clientId,
  title: boardCase.title,
  description: boardCase.description,
  caseType: boardCase.caseType,
  suggestedDecision: boardCase.suggestedDecision || '',
  links: boardCase.links || [],
  submittedBy: boardCase.submittedBy || '',
  deadline: boardCase.deadline,
  responsibleNameId: boardCase?.responsible?.id || '',
  connectedMeetingId: meetingId || boardCase.connectedMeeting?.id || null,
  ...(boardCase.inboxMessageModel && {
    inboxMessageModel: boardCase.inboxMessageModel,
  }),
})

export const mapBoardCaseToCasePutModel = (
  boardCase: IBoardCaseInputModel
): ICasePutModel => ({
  // To check if we can actually update.
  lastUpdated: boardCase.lastUpdated,
  caseType: boardCase.caseType,
  description: boardCase.description,
  links: boardCase.links,
  responsibleNameId: boardCase.responsible?.id || '',
  submittedBy: boardCase.submittedBy,
  title: boardCase.title,
  connectedMeetingId: boardCase.connectedMeeting?.id || null,
  suggestedDecision: boardCase.suggestedDecision || '',
  deadline: boardCase.deadline,
})

export const mapBoardCaseListToAgendaModel = (
  caseList: IBoardCasesList
): IMeetingAgendaModel[] =>
  caseList.result.map((item) => ({
    caseId: item.id,
    created: item.created,
    title: item.title,
    order: 0,
    description: item.description,
    type: item.caseType,
    submittedBy: item.submittedBy,
    responsible: item.responsible,
    deadline: item.deadline,
    isStandardCase: false,
    status: item.status as unknown as BoardCaseStatusEnum,
    votes: [],
  }))

export const mapBoardCaseInputToAgendaModel = (
  boardCase: IBoardCaseInputModel,
  status: BoardCaseStatusEnum,
  order: number
): IMeetingAgendaModel => ({
  caseId: boardCase.id,
  description: boardCase.description,
  title: boardCase.title,
  type: boardCase.caseType,
  isStandardCase: false,
  order,
  status,
  votes: [],
  deadline: boardCase.deadline,
  responsible: boardCase.responsible ?? undefined,
  submittedBy: boardCase.submittedBy,
  suggestedDecision: boardCase.suggestedDecision,
})

const getEmailDescription = (
  message: IEmailModel | ISmsModel,
  messageType?: MessageTypes
) => {
  if (messageType === 'email')
    return decodeHtmlEntities(
      sanitizeHtml((message as IEmailModel).contentHtml)
    )

  return trimMessageText(message.content)
}

const trimMessageText = (input: string) => {
  if (!input) return ''
  let text = input.trim()
  text = String(text).replace(
    /(<\/p>|<\/h1>|<\/h2>|<\/h3>|<\/h4>|<\/h5>|<br\/>|<br>|<\/br>)/g,
    '\n'
  )
  text = text.replace(/(<[^>]+>|(\[image:[^\]]+\]))/gm, '')
  return text.trim().split('\n').join('<br/>')
}

const sanitizeHtml = (input: string) => {
  const options: Config = {
    ALLOWED_TAGS: ['p', 'br', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5'],
    ALLOWED_ATTR: ['style'],
  }
  return DOMPurify.sanitize(input, options)
}
